import React, { useEffect, useRef, useState } from 'react';
import './ProductsTagline.css';

/**
 * Every product on the account, in display order. Names mirror the `appName`
 * values in AllProduct.tsx — the tile list the portal shows once signed in — so
 * the signed-out promise and the signed-in reality read the same.
 *
 * The first FEATURED_COUNT are named inline; the rest are derived into the
 * "more..." popover, so adding a product here can never leave it unlisted.
 */
const PRODUCTS = [
  'LinenTech',
  'LinenCrew',
  'LinenDesk',
  'EquipMate',
  'LinenConnect',
  'LinenIQ',
];
const FEATURED_COUNT = 4;

interface ProductsTaglineProps {
  /**
   * The product this sign-in was reached from, when there is one — display name,
   * spelled as in PRODUCTS. Rewrites the sentence from a flat list into "your
   * <product> account also opens …", which is the more useful reading once the
   * user already knows which product they are entering, and drops that product
   * from the list so it is never named twice.
   */
  signingInTo?: string | null;
}

const ProductsTagline: React.FC<ProductsTaglineProps> = ({ signingInTo = null }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  // Only treat it as branded if the name is a product we actually list; an
  // unrecognised name would otherwise produce "Your Foo account also opens" over
  // the full six-product list, naming a product the account does not include.
  const branded = signingInTo && PRODUCTS.includes(signingInTo) ? signingInTo : null;
  const others = branded ? PRODUCTS.filter((name) => name !== branded) : PRODUCTS;
  const featured = others.slice(0, FEATURED_COUNT);
  const rest = others.slice(FEATURED_COUNT);

  return (
    <p className="products-tagline">
      {branded ? (
        <>
          Your <strong>{branded}</strong> account also opens{' '}
        </>
      ) : (
        <>Your single account for{' '}</>
      )}
      {featured.map((name, i) => (
        <React.Fragment key={name}>
          {i > 0 && ', '}
          <strong>{name}</strong>
        </React.Fragment>
      ))}
      {rest.length > 0 && (
        <>
          {' and '}
          <span className="products-tagline__more-wrapper" ref={wrapperRef}>
            <button
              type="button"
              className="products-tagline__more-btn"
              onClick={() => setOpen((prev) => !prev)}
              aria-expanded={open}
              aria-haspopup="true"
            >
              more...
            </button>
            {open && (
              <span className="products-tagline__popover" role="menu">
                <span className="products-tagline__popover-arrow" />
                <span className="products-tagline__popover-title">Also includes</span>
                {rest.map((name) => (
                  <span className="products-tagline__popover-item" key={name} role="menuitem">
                    <span className="products-tagline__dot" /> {name}
                  </span>
                ))}
              </span>
            )}
          </span>
        </>
      )}
    </p>
  );
};

export default ProductsTagline;
